import {
  AuditActorTypeSchema,
  FINDING_AUDIT_AGENT_ACTOR,
  FindingAuditActionSchema,
  parseFindingAuditRequestedBy,
  type FindingAuditLog,
  type FindingAuditRequestedBy,
} from '@terra/shared-contracts-findings';
import { formatProviderDisplayName } from './finding-audit-format';

type AuditTranslate = {
  (key: string): string;
  (key: string, values: Record<string, string | number>): string;
};

const GENERIC_SYSTEM_ACTOR_NAMES = new Set([
  '',
  'system',
  'unknown',
  FINDING_AUDIT_AGENT_ACTOR.SYSTEM.toLowerCase(),
]);

const NEO_ACTOR_NAMES = new Set(['neo', 'neo agent']);

export const isGenericSystemActorName = (
  actorName: string | null | undefined,
): boolean =>
  GENERIC_SYSTEM_ACTOR_NAMES.has(actorName?.trim().toLowerCase() ?? '');

export const isNeoActorName = (actorName: string | null | undefined): boolean =>
  NEO_ACTOR_NAMES.has(actorName?.trim().toLowerCase() ?? '');

const ACTIONS_WITH_OWN_REQUESTED_BY = new Set<FindingAuditLog['action']>([
  FindingAuditActionSchema.enum.RETEST_SCHEDULED,
  FindingAuditActionSchema.enum.RETEST_STARTED,
  FindingAuditActionSchema.enum.REMEDIATION_PLAN_CREATED,
  FindingAuditActionSchema.enum.REMEDIATION_PR_CREATED,
  FindingAuditActionSchema.enum.REMEDIATION_PR_MERGED,
]);

const ACTIONS_THAT_INFER_REQUESTED_BY = new Set<FindingAuditLog['action']>([
  FindingAuditActionSchema.enum.RETEST_SCHEDULED,
  FindingAuditActionSchema.enum.RETEST_STARTED,
]);

const ACTIONS_WITH_ACTOR_AS_REQUESTED_BY_FALLBACK = new Set<
  FindingAuditLog['action']
>([
  FindingAuditActionSchema.enum.RETEST_SCHEDULED,
  FindingAuditActionSchema.enum.RETEST_STARTED,
]);

export type FindingAuditActorSource = {
  actorType: FindingAuditLog['actorType'];
  actorId?: string | null;
  actorName: string | null;
  isTerraUser: boolean;
  provider?: string | null;
};

export const toRequestedByActorSource = (
  requestedBy: FindingAuditRequestedBy,
  provider?: string | null,
): FindingAuditActorSource => ({
  actorType: requestedBy.actorType,
  actorId: requestedBy.actorId,
  actorName: requestedBy.actorName ?? null,
  isTerraUser: requestedBy.isTerraUser === true,
  provider,
});

export const readRequestedBy = (
  log: FindingAuditLog,
): FindingAuditRequestedBy | undefined =>
  parseFindingAuditRequestedBy(log.metadata?.requestedBy);

const isHumanOrIntegrationRequester = (
  requestedBy: FindingAuditRequestedBy,
): boolean =>
  requestedBy.actorType === AuditActorTypeSchema.enum.USER ||
  requestedBy.actorType === AuditActorTypeSchema.enum.INTEGRATION;

const toRequesterFromLogActor = (
  log: FindingAuditLog,
): FindingAuditRequestedBy | undefined => {
  if (
    log.actorType !== AuditActorTypeSchema.enum.USER &&
    log.actorType !== AuditActorTypeSchema.enum.INTEGRATION
  ) {
    return undefined;
  }

  return {
    actorType: log.actorType,
    ...(log.actorId ? { actorId: log.actorId } : {}),
    ...(log.actorName ? { actorName: log.actorName } : {}),
    isTerraUser: log.isTerraUser,
  };
};

const requesterFromRetestStatusChange = (
  log: FindingAuditLog,
): FindingAuditRequestedBy | undefined => {
  if (
    log.action !== FindingAuditActionSchema.enum.STATUS_CHANGED ||
    log.newValue !== 'Retest'
  ) {
    return undefined;
  }

  const fromActor = toRequesterFromLogActor(log);

  if (fromActor) {
    return fromActor;
  }

  const fromMetadata = readRequestedBy(log);

  if (fromMetadata && isHumanOrIntegrationRequester(fromMetadata)) {
    return fromMetadata;
  }

  return undefined;
};

const requesterFromLog = (
  log: FindingAuditLog,
): FindingAuditRequestedBy | undefined => {
  const retestStatusRequester = requesterFromRetestStatusChange(log);

  if (retestStatusRequester) {
    return retestStatusRequester;
  }

  if (!ACTIONS_WITH_OWN_REQUESTED_BY.has(log.action)) {
    return undefined;
  }

  const fromMetadata = readRequestedBy(log);

  if (fromMetadata && isHumanOrIntegrationRequester(fromMetadata)) {
    return fromMetadata;
  }

  if (ACTIONS_WITH_ACTOR_AS_REQUESTED_BY_FALLBACK.has(log.action)) {
    return toRequesterFromLogActor(log);
  }

  return undefined;
};

const leftRetestBetween = ({
  logs,
  fromTime,
  toTime,
}: {
  logs: FindingAuditLog[];
  fromTime: number;
  toTime: number;
}): boolean =>
  logs.some((candidate) => {
    if (candidate.action !== FindingAuditActionSchema.enum.STATUS_CHANGED) {
      return false;
    }

    if (candidate.oldValue !== 'Retest') {
      return false;
    }

    const candidateTime = new Date(candidate.createdAt).getTime();

    return candidateTime > fromTime && candidateTime <= toTime;
  });

export const resolveRequestedBy = ({
  log,
  logs,
}: {
  log: FindingAuditLog;
  logs: FindingAuditLog[];
}): FindingAuditRequestedBy | undefined => {
  const ownRequester = requesterFromLog(log);

  if (ownRequester) {
    return ownRequester;
  }

  if (!ACTIONS_THAT_INFER_REQUESTED_BY.has(log.action)) {
    return undefined;
  }

  const logTime = new Date(log.createdAt).getTime();
  const retestRequestLogs = logs
    .filter(
      (candidate) =>
        candidate.id !== log.id &&
        new Date(candidate.createdAt).getTime() <= logTime,
    )
    .map((candidate) => ({
      requester: requesterFromRetestStatusChange(candidate),
      createdAt: new Date(candidate.createdAt).getTime(),
    }))
    .filter(
      (
        candidate,
      ): candidate is {
        requester: FindingAuditRequestedBy;
        createdAt: number;
      } =>
        candidate.requester !== undefined &&
        !leftRetestBetween({
          logs,
          fromTime: candidate.createdAt,
          toTime: logTime,
        }),
    )
    .sort((left, right) => right.createdAt - left.createdAt);

  return retestRequestLogs[0]?.requester;
};

export const getFindingAuditActorDisplay = ({
  actor,
  brandName,
  t,
}: {
  actor: FindingAuditActorSource;
  brandName: string;
  t: AuditTranslate;
}): string => {
  if (actor.actorType === AuditActorTypeSchema.enum.USER) {
    return actor.actorName || t('audit.unknownActor');
  }

  if (actor.actorType === AuditActorTypeSchema.enum.INTEGRATION) {
    if (actor.provider) {
      return formatProviderDisplayName(actor.provider);
    }

    return actor.actorName || t('audit.unknownActor');
  }

  if (isNeoActorName(actor.actorName)) {
    return t('audit.securityAgent');
  }

  if (!isGenericSystemActorName(actor.actorName)) {
    return actor.actorName?.trim() ?? '';
  }

  return t('audit.agentActor', { brandName });
};
